import { useEffect, useState } from "react";

import { useHash } from "@/lib/hooks/use-hash";
import { MEDIA_TYPES } from "~/components/gallery/media-type.const";
import type { MediaType } from "~/components/gallery/photo-gallery.types";

/**
 * Custom hook that manages media type filter state synchronized with URL hash parameters.
 *
 * Reads the current media type from the URL hash and updates the active media type state.
 * Provides a handler to change the media type and update the URL hash accordingly.
 * Supports virtual tours, videos, photos, and all media types.
 *
 * @returns Object containing the active media type and change handler
 */
export const useHashMediaType = () => {
  const [activeMediaType, setActiveMediaType] = useState<MediaType>(
    MEDIA_TYPES.ALL,
  );
  const { updateHash, hash, hashMap } = useHash();

  useEffect(() => {
    // Read media type from URL hash and update state accordingly
    const type = hashMap.get("type");
    if (type === "virtual_tour") {
      setActiveMediaType(MEDIA_TYPES.VIRTUAL_TOUR);
    } else if (type === "videos") {
      setActiveMediaType(MEDIA_TYPES.VIDEOS);
    } else if (type === "photos") {
      setActiveMediaType(MEDIA_TYPES.PHOTOS);
    } else if (type === "media") {
      setActiveMediaType(MEDIA_TYPES.ALL);
    } else {
      // Keep current state if no valid type in hash
      setActiveMediaType(activeMediaType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  const handleMediaTypeChange = (type: MediaType) => {
    // Update local state and URL hash when media type changes
    setActiveMediaType(type);
    updateHash(`type=${type}`);
  };

  return {
    activeMediaType,
    handleMediaTypeChange,
  };
};
